// frontend/src/components/Settings/AreasSettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { areasAPI, usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import { FiPlus, FiEdit2, FiTrash2, FiChevronRight, FiCheck, FiX } from 'react-icons/fi';
import SearchableSelect from '../Common/SearchableSelect';
import './AreasSettings.css';

// ── helpers ───────────────────────────────────────────────────────────────────
// field_options can arrive as an array or as a JSON string
const optionsOf = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
};
// "Novo, Existente ,N/A" → ['Novo','Existente','N/A']
const parseOptionsText = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

// ── Inline editable row (used by the Areas list on the left) ──────────────────
const EditableRow = ({ item, fields, onSave, onCancel }) => {
  const [form, setForm] = useState(item ? { ...item } : {});
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  return (
    <div className="as-edit-row">
      {fields.map((f) => (
        <input key={f.key} type={f.type || 'text'} value={form[f.key] ?? ''}
          onChange={(e) => set(f.key, e.target.value)}
          placeholder={f.placeholder} className={f.cls || ''} />
      ))}
      <button className="as-btn-save" onClick={() => onSave(form)}><FiCheck size={14} /></button>
      <button className="as-btn-cancel" onClick={onCancel}><FiX size={14} /></button>
    </div>
  );
};

// ── Operation editor: a labelled form panel, grouped, aligned on a grid ───────
const OperationEditor = ({ item, isNew, users, nextSeq, onSave, onCancel, t }) => {
  const [form, setForm] = useState(() => ({
    area_operations_code:     item?.area_operations_code ?? '',
    description:              item?.description ?? '',
    sequence_no:              item?.sequence_no ?? nextSeq,
    bool_field_label:         item?.bool_field_label ?? '',
    field_options:            optionsOf(item?.field_options).join(', '),
    text_field_label:         item?.text_field_label ?? '',
    default_lead_time_days:   item?.default_lead_time_days ?? 0,
    default_assigned_user_id: item?.default_assigned_user_id ?? null,
    is_active:                item?.is_active ?? true,
  }));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const rootRef = useRef(null);
  const firstRef = useRef(null);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    firstRef.current?.focus();
  }, []);

  const set = (k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
  };

  const submit = async () => {
    const errs = {};
    if (!String(form.area_operations_code).trim()) errs.area_operations_code = t('areas.ed_required', 'Obrigatório');
    if (!String(form.description).trim())          errs.description          = t('areas.ed_required', 'Obrigatório');
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    const ok = await onSave({
      ...form,
      area_operations_code: String(form.area_operations_code).trim(),
      description: String(form.description).trim(),
      sequence_no: form.sequence_no === '' ? null : parseInt(form.sequence_no, 10),
      default_lead_time_days: parseInt(form.default_lead_time_days, 10) || 0,
      bool_field_label: String(form.bool_field_label || '').trim() || null,
      text_field_label: String(form.text_field_label || '').trim() || null,
    });
    if (!ok) setSaving(false); // on success the parent closes the editor
  };

  const onEnter = (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } };
  const chips = parseOptionsText(form.field_options);

  return (
    <div className="as-editor" ref={rootRef}>
      <div className="as-editor-title">
        {isNew
          ? t('areas.ed_new', 'Nova operação')
          : `${t('areas.ed_edit', 'Editar operação')} · ${item?.area_operations_code ?? ''}`}
      </div>

      <div className="as-editor-grid">
        {/* ── Identificação ── */}
        <div className="as-group-title">{t('areas.ed_identity', 'Identificação')}</div>

        <div className="as-f span-2">
          <label>{t('areas.seq', 'Seq')}</label>
          <input type="number" value={form.sequence_no ?? ''} onChange={(e) => set('sequence_no', e.target.value)} onKeyDown={onEnter} />
        </div>
        <div className="as-f span-3">
          <label>{t('areas.code', 'Código')} *</label>
          <input ref={firstRef} type="text" className={errors.area_operations_code ? 'has-error' : ''}
            value={form.area_operations_code} onChange={(e) => set('area_operations_code', e.target.value)} onKeyDown={onEnter} />
          {errors.area_operations_code && <span className="as-f-error">{errors.area_operations_code}</span>}
        </div>
        <div className="as-f span-7">
          <label>{t('areas.label', 'Designação')} *</label>
          <input type="text" className={errors.description ? 'has-error' : ''}
            value={form.description} onChange={(e) => set('description', e.target.value)} onKeyDown={onEnter} />
          {errors.description && <span className="as-f-error">{errors.description}</span>}
        </div>

        {/* ── Campos adicionais ── */}
        <div className="as-group-title">{t('areas.ed_extra', 'Campos adicionais (opcionais)')}</div>

        <div className="as-f span-4">
          <label>{t('areas.ed_opt_label', 'Etiqueta do campo de opções')}</label>
          <input type="text" placeholder="Ex: Material" value={form.bool_field_label ?? ''}
            onChange={(e) => set('bool_field_label', e.target.value)} onKeyDown={onEnter} />
        </div>
        <div className="as-f span-8">
          <label>{t('areas.ed_opts', 'Opções (separadas por vírgula)')}</label>
          <input type="text" placeholder="Ex: Novo, Existente, N/A" value={form.field_options}
            onChange={(e) => set('field_options', e.target.value)} onKeyDown={onEnter} />
          {chips.length > 0 && (
            <div className="as-chips">
              {chips.map((c, i) => <span key={`${c}-${i}`} className="as-chip">{c}</span>)}
            </div>
          )}
        </div>
        <div className="as-f span-12">
          <label>{t('areas.ed_text_label', 'Etiqueta do campo de texto')}</label>
          <input type="text" placeholder="Ex: Observação" value={form.text_field_label ?? ''}
            onChange={(e) => set('text_field_label', e.target.value)} onKeyDown={onEnter} />
        </div>

        {/* ── Prazo e responsável ── */}
        <div className="as-group-title">{t('areas.ed_deadline', 'Prazo e responsável')}</div>

        <div className="as-f span-3">
          <label>{t('areas.ed_lead', 'Prazo (dias)')}</label>
          <input type="number" min="0" value={form.default_lead_time_days ?? 0}
            onChange={(e) => set('default_lead_time_days', e.target.value)} onKeyDown={onEnter} />
        </div>
        <div className="as-f span-6">
          <label>{t('areas.responsible', 'Responsável')}</label>
          <SearchableSelect
            value={form.default_assigned_user_id ? String(form.default_assigned_user_id) : ''}
            onChange={(val) => set('default_assigned_user_id', val ? parseInt(val, 10) : null)}
            options={users}
            getOptionLabel={(u) => u.full_name || u.username}
            getOptionValue={(u) => String(u.user_id)}
            placeholder={t('areas.ed_no_user', '— sem responsável —')}
          />
        </div>
        <div className="as-f span-3 as-f-check">
          <label className="as-check-label">
            <input type="checkbox" checked={!!form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
            {t('areas.ed_active', 'Operação ativa')}
          </label>
        </div>
      </div>

      <div className="as-editor-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
          {t('cancel', 'Cancelar')}
        </button>
        <button type="button" className="btn-primary" onClick={submit} disabled={saving}>
          <FiCheck size={14} /> {saving ? t('saving', 'A guardar…') : t('save', 'Guardar')}
        </button>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const AreasSettings = () => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();

  const [areas,        setAreas]        = useState([]);
  const [operations,   setOperations]   = useState([]);
  const [areaUsers,    setAreaUsers]    = useState([]);
  const [allUsers,     setAllUsers]     = useState([]);
  const [selectedArea, setSelectedArea] = useState(null);
  const [loading,      setLoading]      = useState(true);

  // edit state
  const [editingArea, setEditingArea] = useState(null); // 'new' | public_id | null
  const [editingOp,   setEditingOp]   = useState(null); // 'new' | public_id | null

  useEffect(() => {
    Promise.all([areasAPI.getAll(), usersAPI.getAll()])
      .then(([ar, ur]) => {
        if (ar.data.success) setAreas(ar.data.data);
        if (ur.data.success) setAllUsers(ur.data.users || []);
      })
      .catch(() => toast_error(t('messages.error_load', 'Failed to load')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectArea = async (area) => {
    setSelectedArea(area);
    setEditingOp(null);
    try {
      const [or, ur] = await Promise.all([
        areasAPI.getOperations(area.public_id),
        areasAPI.getUsers(area.public_id),
      ]);
      if (or.data.success) setOperations(or.data.data);
      if (ur.data.success) setAreaUsers(ur.data.data);
    } catch {
      toast_error(t('messages.error_load', 'Failed to load'));
    }
  };

  // ── Areas ──
  const saveArea = async (form) => {
    try {
      if (editingArea === 'new') {
        const r = await areasAPI.create(form);
        if (r.data.success) { setAreas((p) => [...p, r.data.data]); toast_success(t('messages.saved', 'Saved')); }
      } else {
        const r = await areasAPI.update(editingArea, form);
        if (r.data.success) {
          setAreas((p) => p.map((a) => (a.public_id === editingArea ? r.data.data : a)));
          if (selectedArea?.public_id === editingArea) setSelectedArea(r.data.data);
          toast_success(t('messages.saved', 'Saved'));
        }
      }
    } catch { toast_error(t('messages.error_save', 'Failed to save')); }
    setEditingArea(null);
  };

  const deleteArea = async (publicId) => {
    if (!window.confirm(t('confirm_delete', 'Delete this area?'))) return;
    try {
      await areasAPI.delete(publicId);
      setAreas((p) => p.filter((a) => a.public_id !== publicId));
      if (selectedArea?.public_id === publicId) { setSelectedArea(null); setOperations([]); setAreaUsers([]); }
      toast_success(t('messages.deleted', 'Deleted'));
    } catch { toast_error(t('messages.error_delete', 'Failed to delete')); }
  };

  // ── Operations ──
  // Returns true on success (the editor then closes); on failure the editor
  // stays open with everything the user typed, so nothing is lost.
  const saveOperation = async (rawForm) => {
    const form = { ...rawForm };
    const parts = parseOptionsText(form.field_options);
    form.field_options = parts.length > 0 ? parts : null;
    try {
      if (editingOp === 'new') {
        const r = await areasAPI.createOperation(selectedArea.public_id, form);
        if (r.data.success) { setOperations((p) => [...p, r.data.data]); toast_success(t('messages.saved', 'Saved')); }
      } else {
        const r = await areasAPI.updateOperation(editingOp, form);
        if (r.data.success) {
          setOperations((p) => p.map((o) => (o.public_id === editingOp ? r.data.data : o)));
          toast_success(t('messages.saved', 'Saved'));
        }
      }
      setEditingOp(null);
      return true;
    } catch (err) {
      toast_error(err?.response?.data?.error || t('messages.error_save', 'Failed to save'));
      return false;
    }
  };

  const deleteOperation = async (publicId) => {
    if (!window.confirm(t('confirm_delete', 'Delete this operation?'))) return;
    try {
      await areasAPI.deleteOperation(publicId);
      setOperations((p) => p.filter((o) => o.public_id !== publicId));
      toast_success(t('messages.deleted', 'Deleted'));
    } catch { toast_error(t('messages.error_delete', 'Failed to delete')); }
  };

  const areaFields = [
    { key: 'area_code',   placeholder: t('areas.code', 'Code'), cls: 'as-input-sm' },
    { key: 'description', placeholder: t('areas.description', 'Description'), cls: 'as-input-lg' },
    { key: 'sequence_no', type: 'number', placeholder: t('areas.seq', 'Seq'), cls: 'as-input-xs' },
  ];

  const nextSeq = operations.reduce((m, o) => Math.max(m, parseInt(o.sequence_no, 10) || 0), 0) + 10;

  if (loading) return <div className="settings-page"><div className="loading">Loading…</div></div>;

  return (
    <div className="settings-page areas-settings">
      <div className="page-header">
        <div>
          <h2>{t('areas.title', 'Areas & Operations')}</h2>
          <p className="page-subtitle">{t('areas.subtitle', 'Define work areas, tasks and assign responsible users')}</p>
        </div>
      </div>

      <div className="as-layout">
        {/* ── Left: Areas list ── */}
        <div className="as-panel as-panel-areas">
          <div className="as-panel-header">
            <span className="as-panel-title">{t('areas.areas', 'Areas')}</span>
            <button className="btn-primary btn-sm" onClick={() => setEditingArea('new')}>
              <FiPlus size={14} /> {t('add', 'Add')}
            </button>
          </div>

          {editingArea === 'new' && (
            <EditableRow fields={areaFields} onSave={saveArea} onCancel={() => setEditingArea(null)} />
          )}

          <div className="as-list">
            {areas.map((area) => (
              editingArea === area.public_id
                ? <EditableRow key={area.public_id} item={area} fields={areaFields}
                    onSave={saveArea} onCancel={() => setEditingArea(null)} />
                : (
                  <div key={area.public_id}
                    className={`as-area-row ${selectedArea?.public_id === area.public_id ? 'selected' : ''} ${!area.is_active ? 'inactive' : ''}`}
                    onClick={() => selectArea(area)}>
                    <div className="as-area-info">
                      <span className="as-area-code">{area.area_code}</span>
                      <span className="as-area-meta">{area.operation_count} ops · {area.user_count} users</span>
                    </div>
                    <div className="as-row-actions">
                      <button className="as-icon-btn" title={t('edit', 'Edit')}
                        onClick={(e) => { e.stopPropagation(); setEditingArea(area.public_id); }}>
                        <FiEdit2 size={13} />
                      </button>
                      <button className="as-icon-btn as-icon-btn-danger" title={t('delete', 'Delete')}
                        onClick={(e) => { e.stopPropagation(); deleteArea(area.public_id); }}>
                        <FiTrash2 size={13} />
                      </button>
                      <FiChevronRight size={14} className="as-chevron" />
                    </div>
                  </div>
                )
            ))}
            {areas.length === 0 && (
              <p className="as-empty">{t('areas.no_areas', 'No areas defined yet')}</p>
            )}
          </div>
        </div>

        {/* ── Right: Operations ── */}
        {selectedArea ? (
          <div className="as-panel as-panel-detail">
            <div className="as-detail-header">
              <h3 className="as-detail-title">
                {selectedArea.area_code}
                {selectedArea.description ? ` — ${selectedArea.description}` : ''}
              </h3>
            </div>

            <div className="as-section">
              <div className="as-section-header">
                <span className="as-section-title">{t('areas.operations', 'Operations')}</span>
                <button className="btn-primary btn-sm" disabled={editingOp !== null} onClick={() => setEditingOp('new')}>
                  <FiPlus size={14} /> {t('add', 'Add')}
                </button>
              </div>

              {editingOp === 'new' && (
                <OperationEditor isNew users={allUsers} nextSeq={nextSeq} t={t}
                  onSave={saveOperation} onCancel={() => setEditingOp(null)} />
              )}

              <div className="as-table-wrap">
                <table className="as-table as-table-ops">
                  <thead>
                    <tr>
                      <th className="as-th-center as-col-seq">{t('areas.seq', 'Seq')}</th>
                      <th className="as-col-code">{t('areas.code', 'Código')}</th>
                      <th>{t('areas.label', 'Designação')}</th>
                      <th>{t('areas.ed_opt_label', 'Etiqueta opções')}</th>
                      <th>{t('areas.field_options', 'Opções')}</th>
                      <th>{t('areas.text_field', 'Campo texto')}</th>
                      <th className="as-th-center as-col-lead">{t('areas.ed_lead', 'Prazo')}</th>
                      <th>{t('areas.responsible', 'Responsável')}</th>
                      <th className="as-col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {operations.map((op) => (
                      editingOp === op.public_id
                        ? (
                          <tr key={op.public_id} className="as-edit-tr">
                            <td colSpan={9}>
                              <OperationEditor item={op} users={allUsers} nextSeq={nextSeq} t={t}
                                onSave={saveOperation} onCancel={() => setEditingOp(null)} />
                            </td>
                          </tr>
                        )
                        : (
                          <tr key={op.public_id} className={!op.is_active ? 'inactive' : ''}>
                            <td className="as-td-center">{op.sequence_no}</td>
                            <td><span className="as-code-badge">{op.area_operations_code}</span></td>
                            <td className="as-td-strong">{op.description}</td>
                            <td>{op.bool_field_label || <span className="as-dash">—</span>}</td>
                            <td>
                              {optionsOf(op.field_options).length
                                ? <div className="as-chips">
                                    {optionsOf(op.field_options).map((c, i) => <span key={`${c}-${i}`} className="as-chip">{c}</span>)}
                                  </div>
                                : <span className="as-dash">—</span>}
                            </td>
                            <td>{op.text_field_label || <span className="as-dash">—</span>}</td>
                            <td className="as-td-center">{op.default_lead_time_days ?? 0}d</td>
                            <td>
                              {op.default_assigned_user_name
                                ? <span className="as-user-cell">{op.default_assigned_user_name}</span>
                                : <span className="as-dash">—</span>}
                            </td>
                            <td className="as-td-actions">
                              <button className="as-icon-btn" title={t('edit', 'Editar')}
                                disabled={editingOp !== null} onClick={() => setEditingOp(op.public_id)}>
                                <FiEdit2 size={13} />
                              </button>
                              <button className="as-icon-btn as-icon-btn-danger" title={t('delete', 'Eliminar')}
                                disabled={editingOp !== null} onClick={() => deleteOperation(op.public_id)}>
                                <FiTrash2 size={13} />
                              </button>
                            </td>
                          </tr>
                        )
                    ))}
                    {operations.length === 0 && editingOp !== 'new' && (
                      <tr><td colSpan={9} className="as-empty-cell">{t('areas.no_ops', 'No operations')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="as-panel as-panel-detail as-panel-empty">
            <p>{t('areas.select_area', 'Select an area to manage its operations and users')}</p>
          </div>
        )}
      </div>

      <div className="toast-container">
        {toasts.map((toast) => <Toast key={toast.id} {...toast} onClose={removeToast} />)}
      </div>
    </div>
  );
};

export default AreasSettings;